/**
 * 一覧の行の「⋮」のメニュー（2026-09-29 #92、docs/10 §4.4 の一覧の表）。
 *   行の主役の動き1つは枠のボタンのまま行に置き、残り（失注・未受注に戻す・もう1枚つくる・一括で請求する など）をここにまとめる。
 *   見た目と動きは右上の「アカウント」メニューと同じ（.pop：右上を軸に 150ms で現れる。項目は 44px）。
 *   丸い 40px の「⋮」のボタン（.rowmore）。読み上げは「{何}のほかの動き」＋aria-expanded。
 *   キーボード：開くと1つ目へ、↓↑・Home・End で移る、Esc で閉じてボタンに戻る、Tab・外を押す・画面が動くと閉じる。
 *   表は横に流せる枠（overflow）の中にあるので、メニューは画面に対して置く（position: fixed。枠で切れない）
 *   2026-09-30（#100）：メニューは document.body の直下に出す（portal。工程表の固定した左の列の重なりの順に隠れない）。下に入らなければ上に開く
 *   2026-10-05（#138）：入れ子（sub）。項目の右に「›」、押す・→・乗せるとその右（入らなければ左）に2つ目のメニュー。
 *   2つ目の中は ↓↑・Home・End、← と Esc で1つ目の項目に戻る。開いている項目は is-open
 *   2026-11-03（#298、docs/102 §2.4.2）：項目は 44px・乗せた行とキーで選んだ行は主色の地＋塗りの上の文字・朱の項目は朱の地（CSS の .pop）。
 *   段の区切りは menuGroups（空の段は詰める）。項目の名前に「…」を付けない（docs/10 §5.1.2・screen-text の tt）
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

export type RowMenuItem = {
  label: string;
  onSelect?: () => void;
  to?: string;
  testId?: string;
  /** 消すなど取り返しの手間がある動き（朱の字。工程表の「削除」#100） */
  danger?: boolean;
  /** 押せない（工程表の出力：工程が無い。#104）。わけは title に */
  disabled?: boolean;
  /** 項目の下に出す1行（#173：押せない取消の「入金を削除すると取消できます」）。読み上げは項目の説明（aria-describedby） */
  note?: string;
  title?: string;
  /** #131：項目の頭のアイコン（20px の線）・右の小さな文字（「外注 3件」「3枚」）・✓ の項目（menuitemcheckbox）・上に区切りの線 */
  icon?: ReactNode;
  hint?: string;
  checked?: boolean;
  divider?: boolean;
  /** to のリンクを押したとき（下書きの PDF：書きかけを送ってから移る）。preventDefault すれば移らない */
  onLink?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** 入れ子のメニュー（#138：見積の行の「内訳へ移動」→ 表紙・内訳の名前）。あれば onSelect・to は使わない */
  sub?: RowMenuItem[];
};
/**
 * 段に分けた項目を1列に（#298、docs/102 §2.4.2）。空の段は詰め、2つ目からの段の頭に区切り線（divider）。
 * 項目の名前の末尾に「…」を付けない（窓が開く項目でも。docs/10 §5.1.2）
 */
export function menuGroups(groups: readonly (readonly RowMenuItem[])[]): RowMenuItem[] {
  const out: RowMenuItem[] = [];
  for (const g of groups) {
    if (!g.length) continue;
    g.forEach((it, k) => out.push(k === 0 && out.length ? { ...it, divider: true } : it));
  }
  return out;
}
type SubPos = { index: number; top: number; left?: number; right?: number };
const itemClass = (it: RowMenuItem, extra = ""): string | undefined =>
  [it.danger ? "danger" : "", it.divider ? "divider" : "", it.icon || it.checked !== undefined ? "withicon" : "", it.note ? "withnote" : "", extra].filter(Boolean).join(" ") || undefined;
function itemBody(it: RowMenuItem, noteId?: string) {
  return (
    <>
      {it.icon ? <span className="mi">{it.icon}</span> : it.checked !== undefined ? <span className="mi">{it.checked ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg> : null}</span> : null}
      {it.note ? (
        <span className="ml">
          <span className="mlt">{it.label}</span>
          {/* 名前は項目の名前だけ・この1行は説明（aria-describedby が指す。aria-hidden でも説明には読まれる） */}
          <span className="mnote" id={noteId} aria-hidden="true">{it.note}</span>
        </span>
      ) : (
        <span className="ml">{it.label}</span>
      )}
      {it.hint ? <span className="mh">{it.hint}</span> : null}
      {it.sub ? (
        <span className="msub" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
        </span>
      ) : null}
    </>
  );
}

export function RowMenu({ label, items, testId, trigger, triggerClass, align = "right", popClass }: {
  label: string;
  items: RowMenuItem[];
  testId?: string;
  /** #131：ボタンの中身（既定は縦の「⋮」）と、ボタンのクラス（既定は rowmore） */
  trigger?: ReactNode;
  triggerClass?: string;
  /** メニューをボタンのどちらの端にそろえるか（既定は右。表の左端のつまみは左＝#131 の2回目） */
  align?: "left" | "right";
  /** メニューの枠に足すクラス（受け取った書類の引き出しの上に出す pop-drawer、S1） */
  popClass?: string;
}) {
  const [pos, setPos] = useState<{ top: number; right?: number; left?: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const subMenu = useRef<HTMLDivElement>(null);
  const id = useId();
  const open = !!pos;
  /** 入れ子のメニュー（#138）。focus＝開いたら1つ目の項目へ（キーボード・押したとき。乗せただけのときは移さない） */
  const [sub, setSub] = useState<SubPos | null>(null);
  const subFocus = useRef(false);
  const subRef = useRef<SubPos | null>(null);
  subRef.current = sub;

  const close = (focusButton: boolean) => {
    setPos(null);
    setSub(null);
    if (focusButton) btn.current?.focus();
  };
  const parentItem = (index: number) => menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]')[index];
  const openSub = (index: number, el: HTMLElement, focus: boolean) => {
    // 縦は項目にそろえ、横は1つ目のメニューの枠の外（右に入らなければ左）。枠に重ねない。
    // 枠の左右は置いた位置（pos）と幅（offsetWidth）から決める（現れる動きの縮みの途中でも同じ値になる）
    const r = el.getBoundingClientRect();
    const w = menu.current?.offsetWidth ?? 0;
    const left = pos?.left != null ? pos.left : window.innerWidth - (pos?.right ?? 0) - w;
    const right = left + w;
    subFocus.current = focus;
    setSub(window.innerWidth - right >= 220 ? { index, top: r.top - 6, left: right + 4 } : { index, top: r.top - 6, right: Math.max(8, window.innerWidth - left + 4) });
  };
  const closeSub = (focusParent: boolean) => {
    const s = subRef.current;
    setSub(null);
    if (focusParent && s) parentItem(s.index)?.focus();
  };
  // 2つ目のメニュー：下に入らなければ上へずらす。開いたら1つ目の項目へ
  useLayoutEffect(() => {
    if (!sub || !subMenu.current) return;
    const h = subMenu.current.offsetHeight;
    if (sub.top + h > window.innerHeight - 8) {
      const top = Math.max(8, window.innerHeight - 8 - h);
      if (top !== sub.top) setSub({ ...sub, top });
    }
  }, [sub]);
  useEffect(() => {
    if (sub && subFocus.current) subMenu.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus();
  }, [sub?.index]); // eslint-disable-line react-hooks/exhaustive-deps
  /** 開いた時刻。押す直前のスクロール（画面の外のボタンを押すとき、スクロールの知らせは押したあとに届く）では閉じない（#131） */
  const openedAt = useRef(0);
  const show = () => {
    openedAt.current = performance.now();
    const r = btn.current!.getBoundingClientRect();
    setPos(align === "left" ? { top: r.bottom + 4, left: Math.max(8, r.left) } : { top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };
  // 下に入りきらないときは、ボタンの上に開く（上にも入らなければ画面の中に収める）。項目の多いメニュー（工程表の行、#100）のため
  useLayoutEffect(() => {
    if (!pos || !menu.current || !btn.current) return;
    const h = menu.current.offsetHeight;
    if (pos.top + h <= window.innerHeight - 8) return;
    const r = btn.current.getBoundingClientRect();
    const top = r.top - 4 - h >= 8 ? r.top - 4 - h : Math.max(8, window.innerHeight - 8 - h);
    if (top !== pos.top) setPos({ ...pos, top });
  }, [pos]);
  // 開いたら1つ目の項目へ（描画を待たずに。すぐに打つキーもメニューが受け取る）
  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !subMenu.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) close(false);
    };
    const onMove = (e: Event) => {
      // メニューの中の送り（窓が低いと項目の多いメニューは枠の中で送れる）では閉じない。
      //   前は End・↓ で下の項目へ移る・ホイールで送ると閉じ、工程表の行の「削除」が押せなかった
      if (e.target instanceof Node && (menu.current?.contains(e.target) || subMenu.current?.contains(e.target))) return;
      if (performance.now() - openedAt.current < 250) return;
      close(false);
    };
    // 開いている間は、どこにフォーカスがあっても Esc で閉じる。表のセルが（行を開く Enter を止めるために）キーの伝わりを止めているので、
    // document の「捕まえる段階」（capture）で聞く
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (subRef.current) closeSub(true); // 入れ子が開いていれば、それだけ閉じて1つ目の項目へ
      else close(true);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc, true);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc, true);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  const onKey = (e: KeyboardEvent) => {
    const xs = Array.from(menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    const i = xs.indexOf(document.activeElement as HTMLElement);
    const go = (j: number) => {
      e.preventDefault();
      xs[(j + xs.length) % xs.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(xs.length - 1);
    else if (e.key === "Tab") close(false); // Esc は document で見る（上の useEffect）
    else if (e.key === "ArrowRight") {
      const el = document.activeElement as HTMLElement | null;
      const k = el?.dataset.subIndex;
      if (k != null) {
        e.preventDefault();
        openSub(Number(k), el!, true);
      }
    }
  };
  const onSubKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    const xs = Array.from(subMenu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    const i = xs.indexOf(document.activeElement as HTMLElement);
    const go = (j: number) => {
      e.preventDefault();
      xs[(j + xs.length) % xs.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(xs.length - 1);
    else if (e.key === "ArrowLeft") {
      e.preventDefault();
      closeSub(true);
    } else if (e.key === "Tab") close(false);
  };
  const subItems = sub ? (items[sub.index]?.sub ?? []) : [];

  if (!items.length) return null;
  return (
    <span className="rowmenu" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        ref={btn}
        className={triggerClass ?? "rowmore"}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close(false) : show())}
        data-testid={testId}
      >
        {trigger ?? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
        )}
      </button>
      {pos ? createPortal(
        <div className={popClass ? `pop ${popClass}` : "pop"} id={id} role="menu" aria-label={label} ref={menu} style={pos.left != null ? { top: pos.top, left: pos.left, right: "auto", transformOrigin: "top left" } : { top: pos.top, right: pos.right }} onKeyDown={onKey}>
          {items.map((it, k) => {
            const body = itemBody(it);
            const cls = itemClass(it, sub?.index === k ? "is-open" : "");
            if (it.sub) {
              return (
                <button
                  key={it.label}
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={sub?.index === k}
                  className={cls}
                  data-testid={it.testId}
                  data-sub-index={k}
                  disabled={it.disabled}
                  aria-disabled={it.disabled || undefined}
                  title={it.title}
                  onMouseEnter={(e) => sub?.index !== k && openSub(k, e.currentTarget, false)}
                  // 押したときは開いて1つ目へ（乗せて開いたあとに押しても閉じない＝乗せる・押すの順が前後しても同じ）
                  onClick={(e) => (sub?.index === k ? subMenu.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus() : openSub(k, e.currentTarget, true))}
                >
                  {body}
                </button>
              );
            }
            return it.to ? (
              <Link
                key={it.label}
                role="menuitem"
                to={it.to}
                className={cls}
                data-testid={it.testId}
                // 自分で移る項目（onLink＝書きかけの保存を確かめてから移る PDF など）は、離れるときの確かめ（BackButton の useLeaveGuard）を通さない（#177）
                data-leave={it.onLink ? "own" : undefined}
                onMouseEnter={() => sub && setSub(null)}
                onClick={(e) => {
                  close(false);
                  it.onLink?.(e);
                }}
              >
                {body}
              </Link>
            ) : (
              <button
                key={it.label}
                type="button"
                role={it.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
                aria-checked={it.checked}
                className={cls}
                data-testid={it.testId}
                disabled={it.disabled}
                aria-disabled={it.disabled || undefined}
                aria-describedby={it.note ? `${id}-n${k}` : undefined}
                title={it.title}
                onMouseEnter={() => sub && setSub(null)}
                onClick={() => {
                  close(false);
                  it.onSelect?.();
                }}
              >
                {it.note ? itemBody(it, `${id}-n${k}`) : body}
              </button>
            );
          })}
        </div>
      , document.body) : null}
      {pos && sub && subItems.length ? createPortal(
        <div className="pop popsub" role="menu" aria-label={items[sub.index]?.label} ref={subMenu} style={sub.left != null ? { top: sub.top, left: sub.left, right: "auto", transformOrigin: "top left" } : { top: sub.top, right: sub.right }} onKeyDown={onSubKey} data-testid={items[sub.index]?.testId ? `${items[sub.index]!.testId}-sub` : undefined}>
          {subItems.map((it, k) =>
            // #172：入れ子の項目もリンクにできる（請求書の「⋯」の「入金 n件 ›」→ その入金の画面）
            it.to ? (
              <Link key={`${k}:${it.label}`} role="menuitem" to={it.to} className={itemClass(it)} data-testid={it.testId} title={it.title} onClick={() => close(false)}>
                {itemBody(it)}
              </Link>
            ) : (
              <button
                key={`${k}:${it.label}`}
                type="button"
                role="menuitem"
                className={itemClass(it)}
                data-testid={it.testId}
                disabled={it.disabled}
                aria-disabled={it.disabled || undefined}
                title={it.title}
                onClick={() => {
                  close(false);
                  it.onSelect?.();
                }}
              >
                {itemBody(it)}
              </button>
            ),
          )}
        </div>
      , document.body) : null}
    </span>
  );
}

/**
 * 押した所に開くメニュー（#299、docs/102 §5.10：工程表の右クリック・見出しの長押し）。見た目と動きは RowMenu と同じ（.pop・項目 44px・✓ の項目）。
 *   開くと1つ目の項目へ、↓↑・Home・End で移る、Esc・Tab・外を押す・画面が動くと閉じる（閉じたら onClose）。入れ子は持たない
 */
export function PointMenu({ at, label, items, onClose, testId }: { at: { x: number; y: number }; label: string; items: RowMenuItem[]; onClose: () => void; testId?: string }) {
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: at.y + 2, left: at.x + 2 });
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // 下・右に入らなければ押した所の上・左へ（画面の中に収める）
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    const top = at.y + 2 + h <= window.innerHeight - 8 ? at.y + 2 : Math.max(8, at.y - 2 - h);
    const left = at.x + 2 + w <= window.innerWidth - 8 ? at.x + 2 : Math.max(8, at.x - 2 - w);
    setPos({ top, left });
  }, [at.x, at.y]);
  useEffect(() => {
    menu.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus();
    const openedAt = performance.now();
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) closeRef.current();
    };
    const onMove = (e: Event) => {
      if (e.target instanceof Node && menu.current?.contains(e.target)) return;
      if (performance.now() - openedAt < 250) return;
      closeRef.current();
    };
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc, true);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc, true);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, []);
  const onKey = (e: KeyboardEvent) => {
    const xs = Array.from(menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    const i = xs.indexOf(document.activeElement as HTMLElement);
    const go = (j: number) => {
      e.preventDefault();
      xs[(j + xs.length) % xs.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(xs.length - 1);
    else if (e.key === "Tab") closeRef.current();
  };
  if (!items.length) return null;
  return createPortal(
    <div className="pop" role="menu" aria-label={label} ref={menu} style={{ top: pos.top, left: pos.left, right: "auto", transformOrigin: "top left" }} onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()} data-testid={testId}>
      {items.map((it) => (
        <button
          key={it.label}
          type="button"
          role={it.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
          aria-checked={it.checked}
          className={itemClass(it)}
          data-testid={it.testId}
          disabled={it.disabled}
          aria-disabled={it.disabled || undefined}
          title={it.title}
          onClick={() => {
            closeRef.current();
            it.onSelect?.();
          }}
        >
          {itemBody(it)}
        </button>
      ))}
    </div>,
    document.body,
  );
}
